/**
 * Crew card — shared between `MeetCrewModal` (modal grid) and
 * `CrewPanel` (workshop dashboard panel). Ported faithfully from the
 * legacy `renderCrewModal` card in `_legacy/ws4-modals.js`
 * lines 225-282: a HUMAN row (avatar circle with initials + name +
 * badge pill + role + bio) and, when `member.agent` is present, a
 * paired AGENT row (smart_toy icon + agent name + type + green
 * pulse + description).
 *
 * The per-member accent (`member.color` / `member.agent.color`)
 * drives the avatar border, badge tint, and agent row border so each
 * card carries a distinct hue while sharing the same chrome.
 */
import { Bot } from "lucide-react";
import {
  crewAccentRgb,
  crewAccentVar,
  type CrewMember,
} from "./crew-content";

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join("");
}

export function CrewCard({ member }: { member: CrewMember }) {
  const hc = crewAccentVar(member.color);
  const hr = crewAccentRgb(member.color);
  const agentColor = member.agent?.color ?? member.color;
  const agc = crewAccentVar(agentColor);
  const agr = crewAccentRgb(agentColor);

  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{
        background: "var(--bg-panel-alt)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
      }}
    >
      {/* HUMAN row */}
      <div
        className="px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.1)" }}
      >
        <div className="mb-2 flex items-center gap-2.5">
          <div
            aria-hidden
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full"
            style={{
              background: `rgba(${hr}, 0.15)`,
              border: `2px solid ${hc}`,
            }}
          >
            <span
              className="font-sans font-bold"
              style={{ color: hc, fontSize: "var(--fs-sub)" }}
            >
              {initials(member.name)}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center gap-1.5">
              <span
                className="font-sans font-semibold"
                style={{ color: "var(--text-primary)", fontSize: "var(--fs-base)" }}
              >
                {member.name}
              </span>
              {member.badge && (
                <span
                  className="rounded-md px-1.5 py-px font-sans font-semibold tracking-[0.08em]"
                  style={{
                    fontSize: "var(--fs-micro)",
                    background: `rgba(${hr}, 0.12)`,
                    border: `1px solid ${hc}`,
                    color: hc,
                  }}
                >
                  {member.badge}
                </span>
              )}
            </div>
            <div
              className="font-sans"
              style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
            >
              {member.role}
            </div>
          </div>
        </div>
        {member.bio && (
          <div
            className="font-sans"
            style={{
              color: "var(--text-secondary)",
              fontSize: "var(--fs-base)",
              lineHeight: 1.55,
            }}
          >
            {member.bio}
          </div>
        )}
      </div>

      {/* AGENT row — only rendered when a paired agent exists */}
      {member.agent && (
        <div
          className="px-4 py-2.5"
          style={{ background: "rgba(var(--accent-true-blue-rgb), 0.03)" }}
        >
          <div className="mb-1.5 flex items-center gap-2">
            <div
              aria-hidden
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md"
              style={{
                background: `rgba(${agr}, 0.12)`,
                border: `1px solid ${agc}`,
              }}
            >
              <Bot className="h-4 w-4" style={{ color: agc }} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span
                  className="font-sans font-semibold tracking-[0.04em]"
                  style={{ color: agc, fontSize: "var(--fs-micro)" }}
                >
                  {member.agent.name}
                </span>
                <span
                  className="font-sans"
                  style={{ color: "var(--text-muted)", fontSize: "var(--fs-micro)" }}
                >
                  ·
                </span>
                <span
                  className="font-sans"
                  style={{ color: "var(--text-muted)", fontSize: "var(--fs-micro)" }}
                >
                  {member.agent.type}
                </span>
                <span
                  aria-hidden
                  className="ml-1 inline-block"
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "var(--accent-green)",
                    boxShadow: "0 0 4px var(--accent-green)",
                    animation: "pulse 2s infinite",
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className="font-sans"
            style={{
              color: "var(--text-secondary)",
              fontSize: "var(--fs-base)",
              lineHeight: 1.5,
            }}
          >
            {member.agent.description}
          </div>
        </div>
      )}
    </div>
  );
}
